import { useState } from 'react';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import { styled } from '@mui/material/styles';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreMenu from '@components/MoreMenu';
import { Typography } from '@components/Typography';
import { Table, getPaginationLabels, HintedHeader, ToggleCell } from '@components/Table';
import { useTranslation } from '@hooks';
import { formatPercentNumber } from '@helpers/formatNumbers';
import ExportCSV from '@features/Analytics/ExportCSV';

const ProgressTrack = styled('div')({
    width: 64,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'var(--action-hover)',
    overflow: 'hidden',
});

const ProgressFill = styled('div')({
    height: '100%',
    borderRadius: 3,
    backgroundColor: 'var(--primary-main)',
});

const EMPTY = '—';

const hasRowData = row => row.visibilityScore !== null && row.visibilityScore !== undefined;

const Sparkline = ({ values }) => {
    const monthPoints = (values || []).map((value, index) => ({ value, index })).filter(point => point.value != null);
    if (monthPoints.length < 2) return <Typography variant="body2">{EMPTY}</Typography>;

    const width = 80;
    const height = 24;
    const padding = 3;
    const min = Math.min(...monthPoints.map(point => point.value));
    const max = Math.max(...monthPoints.map(point => point.value));
    const range = max - min || 1;
    const points = monthPoints
        .map(point => {
            const x = padding + (point.index / (values.length - 1)) * (width - 2 * padding);
            const y = padding + ((point.value - min) / range) * (height - 2 * padding);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');

    return (
        <svg width={width} height={height}>
            <polyline points={points} fill="none" stroke="var(--primary-main)" strokeWidth="1.5" />
        </svg>
    );
};

const Delta90d = ({ value }) => {
    if (value === null || value === undefined) return <Typography variant="body2">{EMPTY}</Typography>;
    if (value === 0) return <Typography variant="body2">0</Typography>;
    const improved = value < 0;
    return (
        <Typography variant="body2" sx={{ color: improved ? 'var(--success-main)' : 'var(--error-main)' }}>
            {improved ? '▲' : '▼'} {Math.abs(value).toFixed(1)}
        </Typography>
    );
};

const getColumns = t => [
    {
        key: 'location',
        component: (
            <Typography variant="subtitle2" align="left">
                {t('AnalyticsVisibility.location')}
            </Typography>
        ),
    },
    {
        key: 'visibility',
        sortKey: 'visibility',
        align: 'left',
        component: <Typography variant="subtitle2">{t('AnalyticsVisibility.visibility')}</Typography>,
    },
    {
        key: 'avgRank',
        sortKey: 'avgRank',
        align: 'left',
        component: <Typography variant="subtitle2">{t('AnalyticsVisibility.avgRank')}</Typography>,
    },
    {
        key: 'rankTrend',
        align: 'left',
        component: (
            <HintedHeader label={t('AnalyticsVisibility.rankTrend')} hint={t('AnalyticsVisibility.rankTrendHint')} />
        ),
    },
    {
        key: 'delta90d',
        sortKey: 'delta90d',
        align: 'left',
        component: (
            <HintedHeader label={t('AnalyticsVisibility.delta90d')} hint={t('AnalyticsVisibility.delta90dHint')} />
        ),
    },
    {
        key: 'autoScan',
        align: 'center',
        component: (
            <HintedHeader label={t('AnalyticsVisibility.autoScan')} hint={t('AnalyticsVisibility.autoScanHint')} />
        ),
    },
    { key: 'actions', component: <Typography variant="subtitle2" /> },
];

const getRows = ({
    items,
    t,
    anchor,
    handleOpenMenu,
    handleCloseMenu,
    getMenuForRow,
    onToggleAutoScan,
    autoScanPendingIds,
}) =>
    items.map(row => {
        const hasData = hasRowData(row);
        // The monthly arm only rescans locations that have already been scanned, so the
        // toggle stays locked until the location is configured or has results.
        const autoScanToggleable = hasData || row.isConfigured;
        const autoScanPending = autoScanPendingIds.includes(row.locationId);
        return [
            {
                value: row.name,
                component: (
                    <Typography variant="subtitle2" align="left">
                        {row.name}
                    </Typography>
                ),
            },
            {
                align: 'left',
                component: hasData ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Typography variant="body2">{formatPercentNumber(row.visibilityScore * 100)}</Typography>
                        <ProgressTrack>
                            <ProgressFill style={{ width: `${Math.min(row.visibilityScore * 100, 100)}%` }} />
                        </ProgressTrack>
                    </div>
                ) : (
                    <Typography variant="body2">{EMPTY}</Typography>
                ),
            },
            {
                align: 'left',
                component: (
                    <Typography variant="body2" align="left">
                        {hasData && row.avgRank != null ? row.avgRank.toFixed(1) : EMPTY}
                    </Typography>
                ),
            },
            {
                align: 'left',
                component: hasData ? (
                    <Sparkline values={row.trend} />
                ) : (
                    <Typography variant="body2">{EMPTY}</Typography>
                ),
            },
            {
                align: 'left',
                component: <Delta90d value={hasData ? row.delta90d : null} />,
            },
            {
                align: 'center',
                component: (
                    <ToggleCell
                        checked={!!row.autoScanEnabled}
                        disabled={!autoScanToggleable || autoScanPending}
                        onChange={event => onToggleAutoScan(row, event.target.checked)}
                        hint={autoScanToggleable ? '' : t('AnalyticsVisibility.autoScanConfigureFirst')}
                    />
                ),
            },
            {
                align: 'right',
                sx: { padding: 0, width: 56 },
                component: (
                    <div onClick={event => event.stopPropagation()}>
                        <MoreMenu
                            id={row.locationId}
                            anchor={anchor}
                            handleOpenEl={handleOpenMenu}
                            handleCloseEl={handleCloseMenu}
                            menu={anchor.id === row.locationId ? getMenuForRow(row) : []}
                        />
                    </div>
                ),
            },
        ];
    });

const VisibilityTable = ({
    data,
    isLoading,
    error,
    sort,
    onSortChange,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    onRunScan,
    onConfigureClick,
    onToggleAutoScan,
    autoScanPendingIds = [],
    onExport,
}) => {
    const [anchor, setAnchor] = useState({ el: null, id: null });
    const { t } = useTranslation();
    const { items = [], totalCount = 0, pages = 0, page = 0, pageSize = 10 } = data || {};

    const handleOpenMenu = (event, id) => setAnchor({ el: event.currentTarget, id });
    const handleCloseMenu = () => setAnchor({ el: null, id: null });

    const getMenuForRow = row => {
        const hasData = hasRowData(row);
        const closeThen = action => () => {
            handleCloseMenu();
            action(row);
        };
        return [
            ...(hasData
                ? [
                      {
                          id: 'view',
                          icon: <VisibilityIcon sx={{ height: '20px', width: '20px' }} />,
                          title: t('AnalyticsVisibility.viewReports'),
                          onClick: closeThen(onRowClick),
                      },
                  ]
                : []),
            ...(hasData || row.isConfigured
                ? [
                      {
                          id: 'run',
                          icon: <PlayArrowRoundedIcon sx={{ height: '20px', width: '20px' }} />,
                          title: t('AnalyticsVisibility.runScan'),
                          onClick: closeThen(onRunScan),
                      },
                  ]
                : []),
            {
                id: 'configure',
                icon: <SettingsRoundedIcon sx={{ height: '20px', width: '20px' }} />,
                title: t('AnalyticsVisibility.configure'),
                onClick: closeThen(onConfigureClick),
            },
        ];
    };

    return (
        <Paper
            elevation={1}
            sx={{
                padding: '24px',
                width: '100%',
                borderRadius: '8px',
                '@media print': {
                    breakInside: 'avoid',
                },
            }}
        >
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t('AnalyticsVisibility.reportTitle')}
                </Typography>
                <ExportCSV type="visibility" apiCall={onExport} />
            </div>
            {error ? (
                <Typography variant="body1" align="center" sx={{ display: 'block', padding: '32px 0' }}>
                    {t('AnalyticsVisibility.loadError')}
                </Typography>
            ) : !isLoading && !items.length ? (
                <Typography variant="body1" align="center" sx={{ display: 'block', padding: '32px 0' }}>
                    {t('AnalyticsVisibility.noDataYet')}
                </Typography>
            ) : (
                <TableContainer>
                    <Table
                        withHover
                        columnNames={getColumns(t)}
                        rows={getRows({
                            items,
                            t,
                            anchor,
                            handleOpenMenu,
                            handleCloseMenu,
                            getMenuForRow,
                            onToggleAutoScan,
                            autoScanPendingIds,
                        })}
                        data={items}
                        isDataLoading={isLoading}
                        sort={sort}
                        onSortChange={onSortChange}
                        onRowClick={row => row && hasRowData(row) && onRowClick(row)}
                        withPagination
                        paginationProps={{
                            colSpan: 7,
                            count: totalCount,
                            page,
                            rowsPerPage: pageSize,
                            rowsPerPageOptions: [10, 20, 50],
                            pages,
                            onPageChange: (event, newPage) => onPageChange(newPage),
                            onRowsPerPageChange: event => onPageSizeChange(parseInt(event.target.value, 10)),
                            ...getPaginationLabels(t),
                        }}
                    />
                </TableContainer>
            )}
        </Paper>
    );
};

export default VisibilityTable;
